import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowUpRight, Check, X } from "lucide-react";
import { SECTORS } from "@/lib/content";

type Sector = (typeof SECTORS)[number];

export default function SectorExplorer() {
  const [active, setActive] = useState<Sector | null>(null);

  useEffect(() => {
    if (!active) return;
    const close = (event: KeyboardEvent) => event.key === "Escape" && setActive(null);
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", close);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", close);
    };
  }, [active]);

  return (
    <section className="relative py-20" data-testid="sector-explorer">
      <div className="container-edx">
        <div className="eyebrow inline-flex"><Icons.Users size={11} className="text-edx-purple" /> Industries we serve</div>
        <h2 className="display mt-5 text-3xl md:text-5xl text-white">Built for <span className="gradient-text">every sector</span>.</h2>
        <p className="mt-4 max-w-3xl text-sm leading-7 text-edx-muted md:text-base">
          Different industries face different constraints. Select a sector to explore how EDENOX can build smarter systems, better digital experiences, and technology that makes work easier.
        </p>

        <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {SECTORS.map((sector, index) => {
            const Icon = (Icons as any)[sector.icon] || Icons.Sparkles;
            return (
              <motion.button
                type="button"
                key={sector.name}
                data-testid={`sector-${index}`}
                onClick={() => setActive(sector)}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.04 }}
                className="sector-card"
              >
                <span className="sector-card-number">0{index + 1}</span>
                <Icon size={22} />
                <strong>{sector.name}</strong>
                <p>{sector.preview}</p>
                <span className="sector-card-action">Explore sector <ArrowUpRight size={14} /></span>
              </motion.button>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            className="sector-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onMouseDown={(event) => event.target === event.currentTarget && setActive(null)}
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="sector-dialog-title"
              data-testid="sector-dialog"
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              className="sector-modal"
            >
              <button type="button" onClick={() => setActive(null)} aria-label="Close sector details" className="sector-modal-close">
                <X size={20} />
              </button>
              <div className="system-label"><span>SECTOR BRIEF</span><i /> EDENOX capability map</div>
              <h3 id="sector-dialog-title">{active.name}</h3>
              <p>{active.intro}</p>
              <div className="sector-solutions">
                <span>How EDENOX can help</span>
                <ul>
                  {active.solutions.map((solution) => <li key={solution}><Check size={14} />{solution}</li>)}
                </ul>
              </div>
              <a href="/consultation" className="btn btn-primary mt-7">Discuss this sector <ArrowUpRight size={15} /></a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
